import { useClinicAddress } from '@hooks/useClinicAddress'
import { useClinicOfficialName } from '@hooks/useClinicOfficialName'
import { useGetTreatmentTitleWithDuration } from '@hooks/useGetTreatmentTitleWithDuration'
import * as React from 'react'
import useStyles from './AppointmentInfo.styles'
import { ReactComponent as ToothIcon } from 'src/images/icon-tooth.svg'
import { ReactComponent as ClinicIcon } from 'src/images/icon-clinic.svg'
import { ReactComponent as LocationIcon } from 'src/images/icon-location-small.svg'
import { ReactComponent as CalendarIcon } from 'src/images/icon-calendar.svg'
import { ReactComponent as TimeIcon } from 'src/images/icon-time.svg'
import { useChoiceState } from '@context/ChoiceContext'
import { format } from 'date-fns'
import AppointmentInfoItem from './AppointmentInfoItem'
import Separator from '@components/Separator'
import { useTranslation } from 'react-i18next'
import { useTreatmentDuration } from '@hooks/useTreatmentDuration'
import AppointmentPractitioner from './AppointmentPractitioner'
import { useDateFnsLocale } from '@hooks/useDateFnsLocale'
import { DATE_FORMATS } from 'src/config/constants'
import { useLanguage } from '@hooks/useLanguage'
import { useAppointmentCalendar } from '@context/AppointmentCalendarContext/AppointmentCalendarContext'
import { useConfig } from '@context/ConfigurationContext'
import { AppMode } from '@customTypes/Generic'
import { isEmpty } from 'lodash'
import getTimeslotHourRange from '@screens/Appointment/helpers/getTimeslotHourRange'

type AppointmentInfoProps = {
  hidePractitionerChangeButton?: boolean
}

const AppointmentInfo = ({ hidePractitionerChangeButton }: AppointmentInfoProps) => {
  const clinicName = useClinicOfficialName()
  const treatementTitle = useGetTreatmentTitleWithDuration()
  const treatmentDuration = useTreatmentDuration()
  const clinicAddress = useClinicAddress()
  const classes = useStyles()
  const { t } = useTranslation()
  const locale = useDateFnsLocale()
  const language = useLanguage()
  const { selectedBookableDateAndTime } = useChoiceState()

  const { mode } = useConfig()
  const isGapFiller = mode === AppMode.GAP_FILLER
  const { isMoreClinicsOpen, currentPeriod } = useAppointmentCalendar()

  return (
    <div className={classes.root}>
      {!isMoreClinicsOpen && (!isEmpty(currentPeriod) || isGapFiller) && (
        <>
          <AppointmentPractitioner hidePractitionerChangeButton={hidePractitionerChangeButton} />
          <Separator />
        </>
      )}
      <AppointmentInfoItem label={t('treatment')} title={treatementTitle} icon={<ToothIcon width="16px" height="16px" />} />
      <AppointmentInfoItem label={t('clinic')} title={clinicName} icon={<ClinicIcon width="16px" height="16px" />} />
      <AppointmentInfoItem label={t('address')} title={clinicAddress} icon={<LocationIcon width="16px" height="16px" />} />
      <AppointmentInfoItem
        label={t('date')}
        placeholder={t('select_timeslot')}
        title={
          selectedBookableDateAndTime?.date &&
          format(new Date(selectedBookableDateAndTime?.date), DATE_FORMATS.DAY_MONTH_YEAR[language], { locale })
        }
        icon={<CalendarIcon width="16px" height="16px" />}
      />
      <AppointmentInfoItem
        label={t('time')}
        placeholder={t('select_timeslot')}
        title={
          selectedBookableDateAndTime && getTimeslotHourRange(locale, selectedBookableDateAndTime, treatmentDuration)
        }
        icon={<TimeIcon width="16px" height="16px" />}
      />
    </div>
  )
}

export default AppointmentInfo
